import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { CrownSimpleIcon, CheckCircleIcon } from "@phosphor-icons/react";

const TEMPLATES = [
  {
    id: "thanks_discount",
    label: "Thanks + 10% off",
    body:
      "Hey {handle} — thanks for your recent order with us. Use code LOYAL10 for 10% off your next purchase. Appreciate you 🙏",
  },
  {
    id: "early_access",
    label: "Early access",
    body:
      "Hey {handle} — dropping a fresh batch in the next 24h. As one of my best buyers I wanted to give you first dibs. Let me know if anything catches your eye!",
  },
  {
    id: "feedback",
    label: "Quick check-in",
    body:
      "Hey {handle} — quick check-in. How did the last lot work out for you? Anything you'd love to see in the next show?",
  },
];

function fmtUSD(cents) {
  if (!Number.isFinite(cents)) return "$0";
  return `$${(cents / 100).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

export default function MessageTopBuyersModal({ open, onOpenChange }) {
  const [buyers, setBuyers] = useState(null); // null = loading
  const [selected, setSelected] = useState(new Set());
  const [text, setText] = useState(TEMPLATES[0].body);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setBuyers(null);
    api
      .get("/me/top-buyers", { params: { limit: 3 } })
      .then((r) => {
        if (cancelled) return;
        const items = r.data.items || [];
        setBuyers(items);
        // Default-select all returned buyers — seller can deselect any.
        setSelected(new Set(items.map((b) => b.user_id)));
      })
      .catch((e) => {
        if (!cancelled) {
          setBuyers([]);
          toast.error(apiError(e));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  const toggle = (uid) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(uid)) next.delete(uid);
      else next.add(uid);
      return next;
    });
  };

  const applyTemplate = (tpl) => setText(tpl.body);

  const renderText = useMemo(() => {
    // Server stores literal text — we don't substitute {handle} per recipient
    // because the broadcast endpoint sends one shared message. Strip the
    // placeholder so the buyer doesn't see "Hey {handle}".
    return (text || "").replace(/{handle}/g, "there");
  }, [text]);

  const send = async () => {
    if (busy) return;
    const ids = Array.from(selected);
    if (ids.length === 0) {
      toast.error("Pick at least one buyer.");
      return;
    }
    if (renderText.trim().length < 5) {
      toast.error("Message is too short.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/dm/broadcast", {
        user_ids: ids,
        text: renderText,
      });
      const okCount = data.delivered || 0;
      const failCount = (data.total || 0) - okCount;
      if (okCount > 0) {
        toast.success(
          failCount > 0
            ? `Sent to ${okCount} buyer${okCount === 1 ? "" : "s"} (${failCount} skipped).`
            : `Sent to ${okCount} buyer${okCount === 1 ? "" : "s"}.`
        );
      } else {
        toast.error("Couldn't deliver any messages — try again later.");
      }
      onOpenChange(false);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display font-black tracking-tight text-lg">
            <CrownSimpleIcon size={18} weight="fill" className="text-amber-500" />
            Message your top buyers
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Buyers */}
          <div>
            <p className="text-[11px] font-mono uppercase tracking-[0.18em] text-zinc-500 mb-2">
              Top spenders · last 90 days
            </p>
            {buyers === null ? (
              <p className="text-sm text-zinc-500">Loading…</p>
            ) : buyers.length === 0 ? (
              <div className="border border-zinc-200 rounded-xl p-4 bg-zinc-50 text-sm text-zinc-600">
                No paid orders yet. Once you've shipped a few orders, your top
                buyers show up here for a one-tap retention DM.
              </div>
            ) : (
              <div className="grid gap-2">
                {buyers.map((b) => {
                  const on = selected.has(b.user_id);
                  return (
                    <button
                      key={b.user_id}
                      onClick={() => toggle(b.user_id)}
                      className={`flex items-center gap-3 p-3 rounded-xl border-2 transition-colors text-left ${
                        on
                          ? "border-[#1D78AB] bg-[#1D78AB]/5"
                          : "border-zinc-200 hover:border-zinc-300"
                      }`}
                      data-testid={`top-buyer-${b.user_id}`}
                    >
                      <div className="h-10 w-10 rounded-full bg-zinc-100 overflow-hidden flex-shrink-0">
                        {b.picture ? (
                          <img src={b.picture} alt="" className="h-full w-full object-cover" />
                        ) : (
                          <div className="h-full w-full grid place-items-center text-zinc-400 font-mono text-xs">
                            @
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-bold text-sm truncate">
                          @{b.username}
                          {b.is_founder && (
                            <span className="ml-1.5 px-1.5 py-0.5 bg-amber-100 text-amber-800 rounded text-[9px] font-bold tracking-wider">
                              FOUNDER
                            </span>
                          )}
                        </div>
                        <div className="text-[11px] text-zinc-500 tabular-nums">
                          {fmtUSD(b.spend_cents)} · {b.order_count} order
                          {b.order_count === 1 ? "" : "s"}
                        </div>
                      </div>
                      {on && (
                        <CheckCircleIcon
                          size={20}
                          weight="fill"
                          className="text-[#1D78AB] flex-shrink-0"
                        />
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Templates */}
          {(buyers || []).length > 0 && (
            <>
              <div>
                <p className="text-[11px] font-mono uppercase tracking-[0.18em] text-zinc-500 mb-2">
                  Pick a template
                </p>
                <div className="flex flex-wrap gap-2">
                  {TEMPLATES.map((t) => (
                    <button
                      key={t.id}
                      onClick={() => applyTemplate(t)}
                      className="px-3 py-1.5 rounded-full text-xs font-bold border border-zinc-300 hover:border-[#1D78AB] hover:text-[#1D78AB] transition-colors"
                      data-testid={`top-buyer-template-${t.id}`}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <p className="text-[11px] font-mono uppercase tracking-[0.18em] text-zinc-500 mb-2">
                  Message
                </p>
                <textarea
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  rows={4}
                  maxLength={1500}
                  className="w-full resize-none rounded-xl border border-zinc-300 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#1D78AB]/30 focus:border-[#1D78AB]"
                  data-testid="top-buyer-message-text"
                />
                <p className="text-[11px] text-zinc-500 mt-1">
                  {text.length}/1500 · Same message goes to each selected buyer.
                  "{`{handle}`}" auto-replaces with "there".
                </p>
              </div>
            </>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            data-testid="top-buyer-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={send}
            disabled={busy || !buyers || buyers.length === 0 || selected.size === 0}
            className="bg-[#1D78AB] hover:bg-[#165d85] text-white"
            data-testid="top-buyer-send"
          >
            {busy
              ? "Sending…"
              : `Send to ${selected.size} buyer${selected.size === 1 ? "" : "s"}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
